<!--
  The dock's settings gear.

  Three preferences that used to be scattered through the command surface — a
  checkbox on the preview card, an underlined toggle in the footer — collected
  behind one gear. The reason they moved is not tidiness: a control sitting on
  the card competes for the same glance and the same thumb as Confirm, and the
  card is the one place where a mis-tap changes a sample. Nothing that only
  changes a setting belongs there.

  There was a fourth, "Fast moves: undo instead of confirm — experiment". The
  experiment is dropped (Andrew, 2026-09-18) and confirm-first stays for every
  mutation, so the row is gone rather than left switched off: a setting nothing
  reads is a promise the dock cannot keep.

  This component owns no preference state. It renders the `prefs` prop and emits
  the whole normalised object on every change; the dock persists it through
  `writePrefs`. One-way, because two sources of truth for "are replies on" is
  exactly how the confirm window ends up timed against the wrong one.
-->
<template>
  <div class="sa-voice-menu">
    <button
      ref="gear"
      type="button"
      class="sa-voice-menu-gear"
      aria-label="Voice settings"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="toggle"
    >
      <!-- Ring, hub and eight teeth. Drawn rather than imported: the app has no
           icon set, and a gear is eleven primitives. -->
      <svg
        class="sa-voice-menu-gear-icon"
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden="true"
        focusable="false"
      >
        <g
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <circle cx="12" cy="12" r="6.6" />
          <circle cx="12" cy="12" r="2.4" />
          <g stroke-width="2">
            <path d="M12 5.4V2.7" />
            <path d="M16.67 7.33l1.91-1.91" />
            <path d="M18.6 12h2.7" />
            <path d="M16.67 16.67l1.91 1.91" />
            <path d="M12 18.6v2.7" />
            <path d="M7.33 16.67l-1.91 1.91" />
            <path d="M5.4 12H2.7" />
            <path d="M7.33 7.33L5.42 5.42" />
          </g>
        </g>
      </svg>
    </button>

    <!-- `role="group"` and not `role="menu"`: the rows are real checkboxes and
         radios, and a menu role would strip their states off them and demand a
         roving tabindex they do not need. Tab reaches every row. -->
    <div
      v-if="open"
      :id="panelId"
      class="sa-voice-menu-panel"
      role="group"
      aria-label="Voice settings"
    >
      <label class="sa-voice-menu-row">
        <span class="sa-voice-menu-text">Spoken replies</span>
        <input
          class="sa-voice-menu-switch"
          type="checkbox"
          role="switch"
          :checked="value.replies"
          @change="set('replies', $event.target.checked)"
        />
      </label>

      <!-- Microphone modes are mutually exclusive choices. -->
      <div
        class="sa-voice-menu-row sa-voice-menu-row-modes"
        role="radiogroup"
        aria-label="Microphone"
      >
        <div class="sa-voice-menu-seg">
          <label
            v-for="option in micModes"
            :key="option.value"
            class="sa-voice-menu-seg-option"
            :class="{ 'sa-voice-menu-seg-on': value.micMode === option.value }"
          >
            <input
              class="sa-voice-menu-seg-input"
              type="radio"
              :name="micGroupName"
              :value="option.value"
              :checked="value.micMode === option.value"
              @change="set('micMode', option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>

      <!-- Read-only, and deliberately under the mode it explains: WHICH
           microphone the browser handed over, and how long that took. macOS
           can hand Chrome an iPhone's Continuity microphone from another room,
           and until this line existed the only place that fact appeared was
           the device menu in Chrome's own address bar. Not a `role="status"`:
           it is settled by the time the panel can be opened, and announcing it
           on open would talk over whatever the user came here to change. -->
      <p v-if="micLine" class="sa-voice-menu-mic">{{ micLine }}</p>

      <label class="sa-voice-menu-row">
        <span class="sa-voice-menu-text">Follow the box to its new position</span>
        <input
          class="sa-voice-menu-switch"
          type="checkbox"
          role="switch"
          :checked="value.follow"
          @change="set('follow', $event.target.checked)"
        />
      </label>

    </div>
  </div>
</template>

<script>
import { normalisePrefs } from "../../utils/voiceDockPrefs.js";

// Ids have to be unique per instance, not per component: the dock renders one
// menu, but a second one on the page would otherwise share a radio group name
// and the two would fight over which mic mode is checked.
let instanceCount = 0;

const MIC_MODE_OPTIONS = [
  { value: "hold", label: "Hold to talk" },
  { value: "tap", label: "Tap to talk" },
  { value: "free", label: "Hands-free" },
];

export default {
  name: "VoiceSettingsMenu",
  props: {
    prefs: { type: Object, required: true },
    // The dock's one-line microphone report, already formatted. Empty until a
    // microphone has been opened, and never a preference: this component emits
    // nothing for it and the user cannot change it here.
    micLine: { type: String, default: "" },
  },
  emits: ["update:prefs"],
  data() {
    instanceCount += 1;
    return {
      open: false,
      uid: instanceCount,
    };
  },
  computed: {
    // Constant, so it stays out of `data` and out of the reactivity system.
    micModes() {
      return MIC_MODE_OPTIONS;
    },
    // Rendered from the normalised view, never from the raw prop, so a partial
    // or stale object from the dock draws as the defaults instead of drawing
    // unchecked boxes for preferences that are actually on.
    value() {
      return normalisePrefs(this.prefs);
    },
    panelId() {
      return `sa-voice-menu-panel-${this.uid}`;
    },
    micGroupName() {
      return `sa-voice-menu-mic-${this.uid}`;
    },
  },
  beforeUnmount() {
    this.detachDismissListeners();
  },
  methods: {
    toggle() {
      if (this.open) {
        this.closeMenu(true);
      } else {
        this.openMenu();
      }
    },

    openMenu() {
      if (this.open) return;
      this.open = true;
      // Capture phase on `document`, and only while the popover is open, so
      // Escape reaches this handler before anything else on the page.
      document.addEventListener("keydown", this.onKeydown, true);
      document.addEventListener("pointerdown", this.onPointerDown, true);
    },

    closeMenu(restoreFocus) {
      if (!this.open) return;
      this.open = false;
      this.detachDismissListeners();
      if (restoreFocus && this.$refs.gear) {
        this.$refs.gear.focus();
      }
    },

    detachDismissListeners() {
      document.removeEventListener("keydown", this.onKeydown, true);
      document.removeEventListener("pointerdown", this.onPointerDown, true);
    },

    onKeydown(event) {
      if (event.key !== "Escape" && event.key !== "Esc") return;
      event.preventDefault();
      // Immediate, not plain `stopPropagation`: the dock's own Escape handler
      // may well be registered on `document` in the capture phase too, and
      // plain propagation stops travelling between nodes, not between two
      // listeners on the same one. Escape closes the innermost thing open, and
      // while this popover is up that is this popover. Implies stopPropagation.
      event.stopImmediatePropagation();
      this.closeMenu(true);
    },

    onPointerDown(event) {
      if (this.$el && this.$el.contains(event.target)) return;
      // Focus goes back to the gear only when it would otherwise be lost with
      // the popover. Yanking it off whatever the user just deliberately clicked
      // would be worse than the problem the rule is there to solve.
      this.closeMenu(this.focusIsInsideMenu());
    },

    focusIsInsideMenu() {
      const active = document.activeElement;
      if (!active || active === document.body) return true;
      return Boolean(this.$el && this.$el.contains(active));
    },

    set(field, next) {
      this.$emit(
        "update:prefs",
        normalisePrefs({ ...this.value, [field]: next })
      );
    },
  },
};
</script>

<style scoped>
/* No Tailwind. public/assets/styles.css is a committed, pre-compiled artefact
   the Vue build does not regenerate, so a utility class not already in it does
   nothing at all. Same reasoning as VoiceCommandModal.vue, same palette. */

.sa-voice-menu {
  position: relative;
  flex: none;
  display: inline-flex;
}

.sa-voice-menu-gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0c4a6e;
  cursor: pointer;
}
.sa-voice-menu-gear:focus-visible {
  outline: 2px solid #0c4a6e;
  outline-offset: -2px;
}

.sa-voice-menu-gear-icon {
  display: block;
}

/* Below and right-aligned to the gear: the gear lives at the end of the dock
   header, so a panel hanging left of its right edge is the only one that stays
   on screen at 320 px. */
.sa-voice-menu-panel {
  position: absolute;
  top: calc(100% + 0.375rem);
  right: 0;
  z-index: 20;
  width: 19rem;
  max-width: calc(100vw - 1.5rem);
  padding: 0.25rem 0.75rem 0.5rem;
  border: 1px solid #0c4a6e;
  border-radius: 12px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  text-align: left;
  box-shadow: 0 8px 24px rgba(12, 74, 110, 0.18);
}

.sa-voice-menu-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  /* The whole row is the target, not the 26 px switch inside it. */
  min-height: 44px;
  padding: 0.375rem 0;
  cursor: pointer;
}

.sa-voice-menu-row + .sa-voice-menu-row {
  border-top: 1px solid #e2e8f0;
}

.sa-voice-menu-text {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}


/* The read-only microphone line. Quieter than the rows above it and separated
   by a rule, because it is a REPORT sitting among controls and a reader
   reaching for a switch must not stop on it. `break-word` because a device
   name is arbitrary text — "Andrew's iPhone Microphone" is not the longest
   thing macOS will hand over. */
.sa-voice-menu-mic {
  margin: 0.125rem 0 0.375rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  color: #64748b;
  overflow-wrap: break-word;
}

/* A switch, built on a real checkbox so it keeps its keyboard behaviour, its
   checked state and its label association. `role="switch"` in the template is
   what tells a screen reader to say on/off rather than checked/unchecked. */
.sa-voice-menu-switch {
  flex: none;
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  width: 46px;
  height: 26px;
  margin: 0;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  cursor: pointer;
}
.sa-voice-menu-switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #0c4a6e;
  transition: transform 120ms ease-out;
}
.sa-voice-menu-switch:checked {
  background: #0c4a6e;
}
.sa-voice-menu-switch:checked::after {
  background: #ffffff;
  transform: translateX(20px);
}
.sa-voice-menu-switch:focus-visible {
  outline: 2px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-voice-menu-row-modes {
  cursor: default;
}

.sa-voice-menu-seg {
  display: flex;
  flex: 1 1 auto;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  overflow: hidden;
}

.sa-voice-menu-seg-option {
  position: relative;
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.25rem 0.5rem;
  background: #ffffff;
  color: #0c4a6e;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}
.sa-voice-menu-seg-on {
  background: #0c4a6e;
  color: #ffffff;
}

/* The radio itself is not drawn; the segment is. It stays in the layout and
   stays focusable, which is why this is clip-and-shrink and not display:none. */
.sa-voice-menu-seg-input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
.sa-voice-menu-seg-input:focus-visible + span {
  outline: 2px solid currentColor;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Nothing here needs to move to be understood. */
@media (prefers-reduced-motion: reduce) {
  .sa-voice-menu-switch::after {
    transition: none;
  }
}

/* A switch drawn with appearance:none disappears under a forced palette, so
   the state is carried by a border there instead of by a fill. */
@media (forced-colors: active) {
  .sa-voice-menu-switch {
    border-color: currentColor;
  }
  .sa-voice-menu-switch::after {
    background: currentColor;
  }
  .sa-voice-menu-seg-on {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }
}
</style>
